/** Geospatial primitives. WGS84 lat/lon in degrees unless noted. Pure — NO i18n/runes,
 *  so the data pipeline (importer/derive/library) that imports this stays testable under
 *  `node --test`. Localised compass/cardinal labels live in `geoLabels.ts`. */

const R = 6371008.8; // mean Earth radius (m)
const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

/**
 * Whether a lat/lon is a real GPS fix. DJI writes (0,0) — "null island" off the
 * coast of Africa — when there is no fix (ATTI mode, no satellites yet, signal
 * loss). No real flight is there, so treat near-(0,0) and non-finite as invalid.
 */
export function gpsValid(lat: number, lon: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lon) && (Math.abs(lat) > 1e-3 || Math.abs(lon) > 1e-3);
}

/** Great-circle distance between two points (m). */
export function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = (lat2 - lat1) * D2R;
  const dLon = (lon2 - lon1) * D2R;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * D2R) * Math.cos(lat2 * D2R) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Initial bearing from point 1 to point 2, degrees clockwise from north [0,360). */
export function bearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = lat1 * D2R;
  const φ2 = lat2 * D2R;
  const Δλ = (lon2 - lon1) * D2R;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) * R2D + 360) % 360;
}

/**
 * Project a lat/lon to local East-North-Up metres relative to an origin, using
 * an equirectangular approximation (accurate to <1% over a few km — fine for a
 * single flight's bounding box). East = +x, North = +y.
 */
export function toENU(
  lat: number,
  lon: number,
  origin: { lat: number; lon: number },
): { e: number; n: number } {
  const e = (lon - origin.lon) * D2R * R * Math.cos(origin.lat * D2R);
  const n = (lat - origin.lat) * D2R * R;
  return { e, n };
}
